import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { v4 as uuid } from "uuid";

import { ApiError } from "./api-error";
import { tokenStore } from "./token-store";

export const API_URL =
  (import.meta.env["VITE_API_URL"] as string | undefined) ?? "http://localhost:3001/api/v1";

interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string;
  };
}

interface RequestExtras {
  /** Opt-in para enviar header `Idempotency-Key` (convenciones §5.5). */
  idempotent?: boolean;
  /** Marca interna para evitar reintento infinito en refresh on 401. */
  __isRetry?: boolean;
}

declare module "axios" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface AxiosRequestConfig extends RequestExtras {}
}

/**
 * Crea una instancia axios configurada con los 3 interceptors del proyecto:
 *  1. Request: inyecta Authorization Bearer + Idempotency-Key opcional.
 *  2. Response error: traduce a `ApiError`; refresca tokens on 401.
 *  3. Response ok: passthrough (el envelope `{ data }` lo desempaca el caller).
 *
 * Exportado como factory para testeabilidad — `apiClient` real es el resultado
 * con el baseURL de entorno.
 */
export function createApiClient(options: {
  baseURL: string;
  onRefreshNeeded?: () => Promise<string | null>;
}): AxiosInstance {
  const instance = axios.create({
    baseURL: options.baseURL,
    withCredentials: true, // necesario para que la cookie de refresh viaje
    headers: { "Content-Type": "application/json" },
    timeout: 30_000,
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = tokenStore.get();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    if (config.idempotent === true) {
      const m = config.method?.toLowerCase();
      // Backend acepta Idempotency-Key en mutaciones POST/PATCH (convenciones §5.5).
      if (m === "post" || m === "patch") {
        config.headers.set("Idempotency-Key", uuid());
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError<ApiErrorBody>) => {
      const config = error.config as (InternalAxiosRequestConfig & RequestExtras) | undefined;

      // Refresh transparente en 401 (excepto en /auth/* para evitar bucles)
      if (
        error.response?.status === 401 &&
        config &&
        !config.__isRetry &&
        !config.url?.includes("/auth/")
      ) {
        const nuevoToken = options.onRefreshNeeded ? await options.onRefreshNeeded() : null;
        if (nuevoToken) {
          config.__isRetry = true;
          config.headers?.set?.("Authorization", `Bearer ${nuevoToken}`);
          return instance.request(config);
        }
      }

      // Sin response → fallo de red
      if (!error.response) {
        return Promise.reject(ApiError.red());
      }

      const body = error.response.data;
      const errObj = body?.error;
      return Promise.reject(
        new ApiError({
          code: errObj?.code ?? "ERROR_DESCONOCIDO",
          message: errObj?.message ?? error.message,
          httpStatus: error.response.status,
          details: errObj?.details,
          requestId: errObj?.requestId,
        }),
      );
    },
  );

  return instance;
}

/**
 * Handler de refresh on 401 del cliente singleton. Arranca en `null` (antes de
 * montar el `AuthProvider`) y lo setea `conectarRefreshHandler`. El ÚNICO
 * interceptor de respuesta (el de `createApiClient`) lo invoca on 401, de modo
 * que el refresh + retry ocurre ANTES de convertir el error a `ApiError`.
 *
 * **Por qué un handler mutable y no un segundo interceptor** (bug histórico):
 * antes el singleton se creaba sin `onRefreshNeeded` y el refresh se "conectaba"
 * agregando un SEGUNDO interceptor de respuesta. Pero el primer interceptor (el
 * de `createApiClient`) corre antes y, al no tener handler, convertía el 401 en
 * `ApiError` y lo rechazaba. El segundo recibía entonces un `ApiError` sin
 * `response.status`, su check de 401 fallaba y NUNCA refrescaba. Resultado: el
 * refresh on 401 no operaba en sesión (solo el bootstrap al recargar la página).
 * Con un único interceptor que lee este handler mutable, el refresh funciona.
 */
let refreshHandler: (() => Promise<string | null>) | null = null;

/**
 * Instancia singleton del cliente — la API pública del módulo.
 * El refresh callback se conecta desde el AuthProvider para evitar
 * dependencia circular (cliente → auth → cliente).
 */
export const apiClient = createApiClient({
  baseURL: API_URL,
  onRefreshNeeded: () => (refreshHandler ? refreshHandler() : Promise.resolve(null)),
});

/**
 * Conectar el callback de refresh tras montar el AuthProvider.
 * Se llama una vez en `app/providers.tsx`. No agrega interceptors: solo setea
 * el handler que usa el interceptor único del cliente.
 */
export function conectarRefreshHandler(handler: () => Promise<string | null>): void {
  refreshHandler = handler;
}
